import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Button,
  Stack
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  PersonAdd,
  ShoppingCart,
  Phone as PhoneIcon,
  Login as LoginIcon,
  Repeat as RepeatIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { apiClient } from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import PeriodSelector from '../components/common/PeriodSelector';
import { computePresetRange, DEFAULT_DATE_RANGE_PRESET } from '../utils/dateRangePresets';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

interface CustomerMetrics {
  totalCustomers: number;
  newCustomers: number;
  activeCustomers: number;
  averageOrderValue: number;
  retentionRate: number;
  growthRate: number;
}

interface TopCustomer {
  id: string;
  name: string;
  email: string;
  mobileNo: string | null;
  customerType: string;
  totalOrders: number;
  totalRevenue: number;
  lastOrderDate: string | null;
  averageOrderValue: number;
}

interface CustomerEngagement {
  totalLogins: number;
  uniqueLogins: number;
  ordersPlaced: number;
  activeCustomers: number;
  averageOrdersPerCustomer: number;
}

interface CustomerDashboardData {
  metrics: CustomerMetrics;
  customerDistribution: Array<{ type: string; count: number; percentage: number }>;
  topCustomers: Array<TopCustomer>;
  activityTrends: Array<any>;
  newCustomersChart: Array<any>;
  engagement: CustomerEngagement;
}

const CustomerAnalyticsDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState(() => computePresetRange(DEFAULT_DATE_RANGE_PRESET));
  const [dashboardData, setDashboardData] = useState<CustomerDashboardData | null>(null);
  const [error, setError] = useState<{ message: string; isForbidden: boolean } | null>(null);

  const fetchDashboardData = useCallback(async () => {
    if (dashboardData) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/admin/analytics/customers/dashboard`, {
        params: { period: 'custom', date_from: dateRange.dateFrom, date_to: dateRange.dateTo }
      });
      setDashboardData(response.data);
    } catch (err: any) {
      const status = err?.response?.status;
      setError({
        message: status === 403
          ? "You don't have permission to view customer analytics."
          : 'Failed to load dashboard data. Please try again.',
        isForbidden: status === 403
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-IN').format(value);
  };

  const handleContactCustomer = (phone: string) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Customer Analytics" subtitle="Comprehensive customer insights and behavior analysis" />
        <Grid container spacing={3} mb={3}>
          {[0, 1, 2].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={300} />
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box>
        <PageHeader title="Customer Analytics" subtitle="Comprehensive customer insights and behavior analysis" />
        <Alert severity={error?.isForbidden ? 'warning' : 'error'} action={
          !error?.isForbidden && (
            <Button color="inherit" size="small" onClick={fetchDashboardData}>Retry</Button>
          )
        }>
          {error?.message || 'Failed to load dashboard data.'}
        </Alert>
      </Box>
    );
  }

  const { metrics, customerDistribution, topCustomers, activityTrends, engagement } = dashboardData;

  return (
    <Box sx={{ opacity: refreshing ? 0.6 : 1, transition: 'opacity 0.15s' }}>
      <PageHeader
        title="Customer Analytics"
        subtitle="Comprehensive customer insights and behavior analysis"
      />

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error.message}
        </Alert>
      )}

      {/* Period Selector */}
      <Box mb={3}>
        <PeriodSelector onChange={(dateFrom, dateTo) => setDateRange({ dateFrom, dateTo })} />
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Customers
                  </Typography>
                  <Typography variant="h4">{formatNumber(metrics.totalCustomers)}</Typography>
                </Box>
                <People sx={{ fontSize: 48, color: '#0088FE', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    New Customers
                  </Typography>
                  <Typography variant="h4">{formatNumber(metrics.newCustomers)}</Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    {metrics.growthRate >= 0 ? (
                      <TrendingUp sx={{ color: 'success.main', mr: 0.5 }} />
                    ) : (
                      <TrendingDown sx={{ color: 'error.main', mr: 0.5 }} />
                    )}
                    <Typography
                      variant="body2"
                      color={metrics.growthRate >= 0 ? 'success.main' : 'error.main'}
                    >
                      {Math.abs(metrics.growthRate).toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
                <PersonAdd sx={{ fontSize: 48, color: '#00C49F', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Active Customers
                  </Typography>
                  <Typography variant="h4">{formatNumber(metrics.activeCustomers)}</Typography>
                  <Typography variant="body2" color="textSecondary" mt={1}>
                    Retention: {metrics.retentionRate.toFixed(1)}%
                  </Typography>
                </Box>
                <ShoppingCart sx={{ fontSize: 48, color: '#FFBB28', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Engagement */}
      {engagement && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Engagement</Typography>
            <Grid container spacing={3}>
              <Grid item xs={6} sm={3}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LoginIcon sx={{ color: '#0088FE', opacity: 0.7 }} />
                  <Box>
                    <Typography variant="h6">{formatNumber(engagement.totalLogins)}</Typography>
                    <Typography variant="caption" color="textSecondary">Total Logins</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <People sx={{ color: '#00C49F', opacity: 0.7 }} />
                  <Box>
                    <Typography variant="h6">{formatNumber(engagement.uniqueLogins)}</Typography>
                    <Typography variant="caption" color="textSecondary">Unique Customers Logged In</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ShoppingCart sx={{ color: '#FFBB28', opacity: 0.7 }} />
                  <Box>
                    <Typography variant="h6">{formatNumber(engagement.ordersPlaced)}</Typography>
                    <Typography variant="caption" color="textSecondary">Orders Placed</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <RepeatIcon sx={{ color: '#8884D8', opacity: 0.7 }} />
                  <Box>
                    <Typography variant="h6">{engagement.averageOrdersPerCustomer.toFixed(2)}</Typography>
                    <Typography variant="caption" color="textSecondary">Avg Orders / Active Customer</Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <Grid container spacing={3} mb={3}>
        {/* Customer Activity Trends */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Activity Trends
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={activityTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="activeCustomers" stroke="#0088FE" name="Active Customers" />
                  <Line type="monotone" dataKey="newCustomers" stroke="#00C49F" name="New Customers" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Customer Type Distribution */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={customerDistribution}
                    dataKey="count"
                    nameKey="type"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {customerDistribution.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Customers Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top Customers by Revenue
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#0d47a1' }}>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Mobile</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Orders</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Revenue</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="right">Avg Order Value</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topCustomers.map((customer) => (
                  <TableRow key={customer.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {customer.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {customer.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={customer.customerType} size="small" />
                    </TableCell>
                    <TableCell>{customer.mobileNo || '-'}</TableCell>
                    <TableCell align="right">{customer.totalOrders}</TableCell>
                    <TableCell align="right">{formatCurrency(customer.totalRevenue)}</TableCell>
                    <TableCell align="right">{formatCurrency(customer.averageOrderValue)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Contact Customer">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleContactCustomer(customer.mobileNo || '')}
                            disabled={!customer.mobileNo}
                            color="primary"
                          >
                            <PhoneIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomerAnalyticsDashboard;
